export type Range = '24h' | '7d' | '30d' | '90d';

export type TimeseriesPoint = {
  date: string;
  registrations: number;
  accounts: number;
  new_users: number;
  net_accounts: number;
  dau: number;
  runs: number;
  pushes_sent: number;
  unsubscribes: number;
};

export type TimeseriesResponse = { range: Range; days: TimeseriesPoint[] };

export type BreakdownEntry = { key: string; count: number };

export type BreakdownsResponse = {
  by_platform: BreakdownEntry[];
  by_app_version: BreakdownEntry[];
  by_locale: BreakdownEntry[];
  by_os_version: BreakdownEntry[];
  by_timezone: BreakdownEntry[];
  by_ram_bucket: BreakdownEntry[];
  by_hardware_id: BreakdownEntry[];
};

export type DeviceInfoSummary = {
  total_devices: number;
  with_hardware: number;
  tablets: number;
  phones: number;
  emulators: number;
  median_memory_mb: number | null;
};

export type DeviceInfoAnalytics = {
  summary: DeviceInfoSummary;
  by_platform: BreakdownEntry[];
  by_os_version: BreakdownEntry[];
  by_device_model: BreakdownEntry[];
  by_brand: BreakdownEntry[];
  by_hardware_id: BreakdownEntry[];
  by_system_name: BreakdownEntry[];
  by_ram_bucket: BreakdownEntry[];
  by_disk_bucket: BreakdownEntry[];
  by_cpu_abis: BreakdownEntry[];
  by_screen: BreakdownEntry[];
  by_api_level: BreakdownEntry[];
  by_tablet: BreakdownEntry[];
  by_emulator: BreakdownEntry[];
};

export type AnalyticsSummary = {
  total_users: number;
  users_notifications_on: number;
  users_notifications_off: number;
  users_push_mobile: number;
  users_push_all: number;
  by_platform: Record<string, number>;
  dau_7d: number;
  registrations_7d: number;
  runs_7d: number;
  runs_by_game_7d: Record<string, number>;
  range: Range;
  delta_dau: number;
  delta_registrations: number;
  delta_runs: number;
  delta_users_notifications_on: number;
};

export type SubscriptionAnalytics = {
  range: Range;
  total_accounts: number;
  active_subscribers: number;
  rc_subscribers: number;
  rc_profile_subscribers: number;
  rc_anonymous_subscribers: number;
  override_only: number;
  subscriber_rate: number;
  new_purchases_in_window: number;
  grant_events_in_window: number;
  expirations_in_window: number;
  auto_renew_on: number;
  auto_renew_off: number;
  lifetime_subscribers: number;
  donation_rewards_granted: number;
  by_store: BreakdownEntry[];
  by_product: BreakdownEntry[];
  delta_new_purchases: number;
};

export type RetentionRow = {
  cohort_week: string;
  cohort_size: number;
  retained: number[];
};

export type RetentionResponse = { weeks: number; rows: RetentionRow[] };

export type TopPlayer = {
  installation_id: string;
  nickname: string | null;
  runs: number;
  total_score: number;
};

export type TopPlayersResponse = {
  range: Range;
  by_runs: TopPlayer[];
  by_score: TopPlayer[];
};

export type GameTrendPoint = { date: string; runs: number };

export type GameTrend = {
  slug: string;
  display_name: string;
  points: GameTrendPoint[];
  total: number;
};

export type GamesTrendResponse = { range: Range; games: GameTrend[] };

export type GameDetail = {
  id: number;
  slug: string;
  display_name: string;
  created_at: string;
  total_runs: number;
  unique_players: number;
  first_run_at: string | null;
  last_run_at: string | null;
  avg_score: number;
  max_score: number | null;
  avg_duration_seconds: number | null;
  runs_7d: number;
  runs_30d: number;
  by_app_version: BreakdownEntry[];
};

export type EngagementSendItem = {
  id: number;
  sent_at: string;
  installation_id: string;
  nickname: string | null;
  platform: string | null;
  campaign_name: string;
  push_key: string;
  title: string | null;
  body: string | null;
};

export type EngagementSendsResponse = {
  items: EngagementSendItem[];
  total: number;
  page: number;
  page_size: number;
};

export type EngagementCampaign = {
  campaign_name: string;
  push_key: string;
  count: number;
};

export type ActivityKind =
  | 'registration'
  | 'run'
  | 'push'
  | 'unsubscribe'
  | 'resource_tap'
  | 'flashcard_view';

export type LearningSummaryResponse = {
  range: Range;
  resource_tap_count: number;
  flashcard_view_count: number;
  distinct_tap_installations: number;
  distinct_view_installations: number;
  delta_resource_taps: number;
  delta_flashcard_views: number;
  delta_distinct_tap_installations: number;
  delta_distinct_view_installations: number;
};

export type LearningTimeseriesPoint = {
  date: string;
  resource_taps: number;
  flashcard_views: number;
};

export type LearningTimeseriesResponse = {
  range: Range;
  days: LearningTimeseriesPoint[];
};

export type LearningBreakdownsResponse = {
  by_resource_key: BreakdownEntry[];
  by_category: BreakdownEntry[];
  by_collection: BreakdownEntry[];
  by_card: BreakdownEntry[];
};

export type LearningEventItem = {
  type: 'tap' | 'view';
  at: string;
  installation_id: string;
  nickname: string | null;
  app_version: string | null;
  resource_key: string | null;
  category: string | null;
  collection: string | null;
  card_title: string | null;
  card_persistency_key: string | null;
};

export type LearningEventsResponse = {
  items: LearningEventItem[];
  total: number;
  page: number;
  page_size: number;
};

export type StreaksSummaryResponse = {
  total_profiles: number;
  active_streaks: number;
  qualified_today: number;
  at_risk: number;
  streak_3_plus: number;
  streak_7_plus: number;
  avg_current_streak: number;
  max_current_streak: number;
  max_longest_streak: number;
  account_profiles: number;
  installation_profiles: number;
  freezes_exhausted: number;
};

export type StreaksDistributionResponse = {
  by_current_streak: BreakdownEntry[];
  by_longest_streak: BreakdownEntry[];
  by_subject: BreakdownEntry[];
};

export type StreakLeaderboardEntry = {
  rank: number;
  current_streak: number;
  longest_streak: number;
  freezes_remaining: number;
  last_qualified_local_date: string | null;
  timezone: string;
  qualified_today: boolean;
  subject_type: 'account' | 'installation';
  account_id: string | null;
  installation_id: string | null;
  display_name: string | null;
  nickname: string | null;
};

export type StreaksLeaderboardResponse = {
  by_current: StreakLeaderboardEntry[];
  by_longest: StreakLeaderboardEntry[];
};

export type StreaksTimeseriesPoint = {
  date: string;
  last_qualified_count: number;
};

export type StreaksTimeseriesResponse = {
  range: Range;
  days: StreaksTimeseriesPoint[];
};

export type AppVersionMetricsDay = {
  date: string;
  counts: Record<string, number>;
};

export type AppVersionMetricsResponse = {
  range: Range;
  versions: string[];
  days: AppVersionMetricsDay[];
  latest_by_version: BreakdownEntry[];
};

export type UpgradeLagStats = {
  count: number;
  median_days: number | null;
  p90_days: number | null;
};

export type AppVersionsSummary = {
  active_devices: number;
  active_ios: number;
  active_android: number;
  on_latest_pct: number | null;
  on_latest_ios_pct: number | null;
  on_latest_android_pct: number | null;
  versions_in_use: number;
  upgrades_observed: number;
  median_days_to_upgrade: number | null;
  p90_days_to_upgrade: number | null;
};

export type OnLatestDay = {
  date: string;
  all: number | null;
  ios: number | null;
  android: number | null;
  active_devices: number;
};

export type VersionShareDay = {
  date: string;
  counts: Record<string, number>;
  shares: Record<string, number>;
};

export type AppVersionsAnalyticsResponse = {
  range: Range;
  latest_by_platform: { ios: string | null; android: string | null };
  summary: AppVersionsSummary;
  on_latest_days: OnLatestDay[];
  version_share_days: VersionShareDay[];
  versions: string[];
  distribution: {
    all: BreakdownEntry[];
    ios: BreakdownEntry[];
    android: BreakdownEntry[];
  };
  behind_latest: BreakdownEntry[];
  upgrade_lag_buckets: BreakdownEntry[];
  upgrade_lag: UpgradeLagStats;
  upgrade_lag_by_platform: Record<string, UpgradeLagStats>;
};

export type CountryMetricsDay = {
  date: string;
  counts: Record<string, number>;
};

export type CountryMetricsResponse = {
  range: Range;
  countries: string[];
  days: CountryMetricsDay[];
};

export type PaywallFunnelStepCount = {
  key: string;
  label: string;
  count: number;
};

export type PaywallFunnelDefinition = {
  id: string;
  label: string;
  steps: PaywallFunnelStepCount[];
};

export type PaywallFunnelsResponse = {
  range: Range;
  funnels: PaywallFunnelDefinition[];
};

export type WebAnalyticsDay = {
  date: string;
  dau: number;
  landing: number;
  app: number;
  cta: number;
  checkout: number;
};

export type WebAnalyticsEventTotal = {
  key: string;
  label: string;
  count: number;
};

export type WebAnalyticsFunnelStep = {
  key: string;
  label: string;
  count: number;
};

export type HeroCardSurface = 'app' | 'web';

export type HeroCardRow = {
  key: string;
  title: string;
  surface: HeroCardSurface;
  taps: number;
  uniques: number;
  source: string | null;
  category: string | null;
};

export type HeroAnalyticsDay = {
  date: string;
  taps: number;
  uniques: number;
};

export type HeroAnalyticsResponse = {
  range: Range;
  taps: number;
  uniques: number;
  delta_taps: number;
  delta_uniques: number;
  app_taps: number;
  web_taps: number;
  unattributed_taps: number;
  days: HeroAnalyticsDay[];
  cards: HeroCardRow[];
  error: string | null;
};

export type WebAnalyticsResponse = {
  range: Range;
  visitors: number;
  dau: number;
  delta_dau: number;
  landing_visitors: number;
  app_visitors: number;
  cta_presses: number;
  store_presses: number;
  checkouts: number;
  days: WebAnalyticsDay[];
  funnel: WebAnalyticsFunnelStep[];
  events: WebAnalyticsEventTotal[];
};

export type AcquisitionFunnelStepCount = {
  key: string;
  label: string;
  count: number;
};

export type AcquisitionFunnelDay = {
  date: string;
  cohort_size: number;
  steps: AcquisitionFunnelStepCount[];
  pending?: boolean;
};

export type AcquisitionBackfillStatus = {
  pending: number;
  complete: number;
  total: number;
  rate_limited_until: string | null;
};

export type AcquisitionFunnelsResponse = {
  step_labels: AcquisitionFunnelStepCount[];
  days: AcquisitionFunnelDay[];
  next_before: string | null;
  has_more: boolean;
  backfill: AcquisitionBackfillStatus;
  amplitude_error?: string | null;
  amplitude_stale_at?: string | null;
};

export type ActivityEvent = {
  kind: ActivityKind;
  at: string;
  installation_id: string;
  nickname: string | null;
  /** IANA zone id from the device record, when known. */
  timezone: string | null;
  summary: string;
};

export type ActivityResponse = { items: ActivityEvent[] };

export type BehaviorEventKind =
  | 'run'
  | 'resource_tap'
  | 'flashcard_view'
  | 'flashcard_set_completion'
  | 'storybook_completion'
  | 'alphabet_sound_listen';

export type BehaviorEvent = {
  kind: BehaviorEventKind;
  at: string;
  installation_id: string;
  nickname: string | null;
  timezone: string | null;
  summary: string;
  duration_seconds: number | null;
  meta: Record<string, unknown>;
};

export type BehaviorTimelineResponse = {
  range: Range;
  items: BehaviorEvent[];
};

export type BehaviorSession = {
  id: string;
  installation_id: string;
  nickname: string | null;
  timezone: string | null;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  event_count: number;
  kinds: string[];
  kind_counts: Record<string, number>;
  primary_kind: string;
  summary: string;
  events: BehaviorEvent[];
};

export type BehaviorSessionsResponse = {
  range: Range;
  gap_minutes: number;
  items: BehaviorSession[];
};

export type BehaviorPulseKpis = {
  active_now: number;
  sessions_24h: number;
  events_24h: number;
  distinct_devices_24h: number;
};

export type PulseHourBucket = {
  hour: number;
  sessions: number;
  events: number;
};

export type PulseHeatmapCell = {
  dow: number;
  hour: number;
  sessions: number;
};

export type BehaviorPulseResponse = {
  as_of: string;
  active_window_minutes: number;
  kpis: BehaviorPulseKpis;
  active_sessions: BehaviorSession[];
  hour_buckets: PulseHourBucket[];
  heatmap: PulseHeatmapCell[];
  recent_sessions: BehaviorSession[];
};

export type StickerPackListItem = {
  id: number;
  slug: string;
  name: string;
  sort_order: number;
  is_published: boolean;
  sticker_count: number;
};

export type AdminSticker = {
  id: number;
  name: string;
  image_url: string;
};

export type AdminStickerPack = {
  id: number;
  slug: string;
  name: string;
  sort_order: number;
  is_published: boolean;
  cover_image_url: string | null;
  stickers: AdminSticker[];
};

export type StickerUploadUrlResponse = {
  presigned_url: string;
  cdn_key: string;
  image_url: string;
};

export type CrosswordListItem = {
  id: number;
  slug: string;
  puzzle_date: string | null;
  served_on_date: string | null;
  queue_order: number | null;
  title: string;
  status: string;
  is_published: boolean;
  clue_count: number;
  needs_review_clue_count: number;
  generated_at: string | null;
};

export type SentencePuzzleListItem = {
  id: number;
  slug: string;
  prompt_en: string;
  target_hmong: string;
  tile_count: number;
  status: string;
  is_published: boolean;
  queue_order: number | null;
  resource_key: string | null;
  game_type: string | null;
  generated_at: string | null;
};

export type DeleteDraftSentencePuzzlesResult = {
  deleted: number;
};

export type DeleteAllSentencePuzzlesResult = {
  deleted: number;
};

export type DeleteSentencePuzzlesResult = {
  deleted: number;
  deleted_ids: number[];
  missing_ids: number[];
};

export type AdminSentencePuzzle = {
  id: number;
  slug: string;
  prompt_translations: Record<string, string>;
  target_hmong: string;
  tiles: string[];
  distractors: string[];
  punctuation_suffix: string;
  status: string;
  is_published: boolean;
  queue_order: number | null;
  resource_key: string | null;
  game_type: string | null;
  approved_at: string | null;
  metadata: Record<string, unknown>;
  generated_with_model: string | null;
  generated_at: string | null;
};

export type BulkImportSentencePuzzlesResult = {
  created: AdminSentencePuzzle[];
  count: number;
  errors: Array<{ row: number; message: string }>;
};

export type AssignSentencePuzzlesResult = {
  updated: number;
  resource_key: string;
  game_type: string;
  updated_ids: number[];
  missing_ids: number[];
  published_ids?: number[];
  publish_errors?: Array<{ row: number; message: string }>;
};

export type PublishSentencePuzzlesResult = {
  published: number;
  published_ids: number[];
  skipped_ids: number[];
  missing_ids: number[];
  errors: Array<{ row: number; message: string }>;
};

export type UnpublishSentencePuzzlesResult = {
  unpublished: number;
  unpublished_ids: number[];
  skipped_ids: number[];
  missing_ids: number[];
};

export type AdminCrossword = {
  id: number;
  slug: string;
  puzzle_date: string | null;
  served_on_date: string | null;
  queue_order: number | null;
  title: string;
  subtitle: string | null;
  status: string;
  is_published: boolean;
  grid: {
    rows: number;
    cols: number;
    cells: Array<{
      row: number;
      col: number;
      solution: string;
      number?: number | null;
    }>;
  };
  clues: {
    across: Array<{
      id: string;
      number: number;
      clue: string;
      answer: string;
      row: number;
      col: number;
      length: number;
      gloss?: string;
    }>;
    down: Array<{
      id: string;
      number: number;
      clue: string;
      answer: string;
      row: number;
      col: number;
      length: number;
      gloss?: string;
    }>;
  };
  metadata: Record<string, unknown>;
};

export type CrosswordSolve = {
  rank: number;
  nickname: string;
  duration_seconds: number;
  played_at: string;
  installation_id: string;
};

export type CrosswordSolvesResponse = {
  solves: CrosswordSolve[];
  count: number;
  fastest_seconds: number | null;
  average_seconds: number | null;
};

export type DonationBucket = '3mo' | '6mo' | '1yr';

export type DonationRedemptionItem = {
  id: number;
  rc_transaction_id: string;
  app_user_id: string;
  product_id: string;
  value_usd: number;
  bucket: string;
  granted_days: number;
  granted: boolean;
  granted_at: string;
  expires_at: string | null;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
};

export type DonationRedemptionSummary = {
  total_redemptions: number;
  granted_count: number;
  distinct_donors: number;
  signed_in_donors: number;
  anonymous_donors: number;
  total_value_usd: number;
  by_bucket: Record<string, number>;
};

export type DonationRedemptionsResponse = {
  items: DonationRedemptionItem[];
  total: number;
  page: number;
  page_size: number;
  summary: DonationRedemptionSummary;
};

export type DictionaryExample = { hmong: string; english: string };

export type DictionarySense = {
  sense_label: string | null;
  definition: string;
  examples: DictionaryExample[];
};

export type DictionaryBrowseEntry = {
  id: number;
  index: number;
  total: number;
  headword: string;
  homonym_index: number | null;
  english: string;
  senses: DictionarySense[];
  scan_page: number | null;
  page_half: string | null;
  raw_text: string;
  review_status: string | null;
  queue_index: number | null;
  queue_total: number | null;
};

export type DictionaryReviewStatus =
  | 'approved'
  | 'rejected'
  | 'bookmark'
  | 'skip';

export type DictionaryListStatusFilter =
  | 'all'
  | 'pending'
  | DictionaryReviewStatus;

export type DictionaryListItem = {
  id: number;
  index: number;
  headword: string;
  homonym_index: number | null;
  english: string;
  review_status: string | null;
};

export type DictionaryListResponse = {
  items: DictionaryListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type FlashcardSetListItem = {
  id: number;
  slug: string;
  name: string;
  mode: string;
  audio_voices: string;
  status: string;
  published_at: string | null;
  sort_order: number;
  content_version: number;
  card_count: number;
  has_cover_card: boolean;
  requires_subscription: boolean;
};

export type AdminFlashcardCard = {
  id: number;
  sort_order: number;
  title: string;
  translations: Record<string, string>;
  card_color: string;
  persistency_key: string;
  image_url: string | null;
  sound_url: string | null;
  sound_children_url: string | null;
  image_cdn_key: string | null;
  sound_adult_cdn_key: string | null;
  sound_children_cdn_key: string | null;
  is_number: boolean;
  is_family: boolean;
  is_pronoun: boolean;
  is_verb: boolean;
  needs_translation_smaller: boolean;
  is_cover: boolean;
};

export type AdminFlashcardSet = {
  id: number;
  slug: string;
  name: string;
  title_translations: Record<string, string>;
  mode: string;
  audio_voices: string;
  status: string;
  published_at: string | null;
  content_version: number;
  sort_order: number;
  has_cover_card: boolean;
  category: string;
  text_color: string;
  background_color: string;
  badge_background: string;
  cover_image_cdn_key: string | null;
  cover_image_url: string | null;
  requires_subscription: boolean;
  cards: AdminFlashcardCard[];
  validation_errors: string[];
};

export type FlashcardMediaUpload = {
  original_filename: string;
  cdn_key: string;
  url: string;
  content_type?: string | null;
  voice?: string | null;
};

export type FlashcardExtractedCard = {
  title: string;
  translations: Record<string, string>;
  card_color: string;
  is_cover: boolean;
  needs_translation_smaller: boolean;
  source_screenshot?: string | null;
};

export type FlashcardMatchResponse = {
  assignments: Array<{
    card_index: number;
    image_filename: string | null;
    sound_adult_filename: string | null;
    sound_children_filename: string | null;
    confidence: number;
    notes: string;
  }>;
  unmatched_images: string[];
  unmatched_audio: string[];
};

export type FlashcardIngestJob = {
  job_id: string;
  status: string;
  result: {
    converted?: Array<{
      original_filename: string;
      raw_cdn_key: string;
      cdn_key: string;
      url?: string;
      voice?: string | null;
    }>;
    cards?: FlashcardExtractedCard[];
    failed?: Array<{
      original_filename?: string;
      cdn_key?: string;
      error: string;
    }>;
  } | null;
  error_message: string | null;
};

export const FLASHCARD_CARD_COLORS = [
  'BLACK',
  'DARK_BLUE',
  'BLUE',
  'GREEN',
  'ORANGE',
  'PINK',
  'PURPLE',
  'RED',
  'WHITE',
  'YELLOW',
  'LIGHT_PURPLE',
  'LIGHT_ORANGE',
  'LIGHT_BLUE',
  'LIGHT_RED',
] as const;

export type TermSheetListItem = {
  id: number;
  slug: string;
  name: string;
  mode: string;
  audio_voices: string;
  status: string;
  published_at: string | null;
  sort_order: number;
  content_version: number;
  category: string;
  term_count: number;
  requires_subscription: boolean;
  flashcard_enabled: boolean;
};

export type StaticTermSheetListItem = {
  kind: 'static';
  resource_key: string;
  title: string;
  category: string;
};

export type AdminPracticeGameTypeStats = {
  game_type: string;
  live_count: number;
  total_count: number;
};

export type AdminResourcePractice = {
  resource_key: string;
  game_types: AdminPracticeGameTypeStats[];
};

export const ASKING_QUESTIONS_RESOURCE_KEY = '/(cheatsheets)/asking-questions';
export const GAME_TYPE_BUILD_SENTENCE = 'build_sentence';
export const GAME_TYPE_MULTIPLE_CHOICE = 'multiple_choice';
export const MULTIPLE_CHOICE_MIN_UNIQUE_ANSWERS = 4;

export type AdminTermSheetTerm = {
  id: number;
  block_id: number;
  sort_order: number;
  title: string;
  translations: Record<string, string>;
  subtitle_translations: Record<string, string>;
  example_translations: Record<string, string>;
  used_for_translations: Record<string, string>;
  examples_translations: Record<string, string>;
  persistency_key: string;
  sound_url: string | null;
  sound_children_url: string | null;
  sound_adult_cdn_key: string | null;
  sound_children_cdn_key: string | null;
};

export type AdminTermSheetBlock = {
  id: number;
  sort_order: number;
  block_type: string;
  title_translations: Record<string, string>;
  body_translations: Record<string, string>;
  terms: AdminTermSheetTerm[];
};

export type AdminTermSheet = {
  id: number;
  slug: string;
  name: string;
  title_translations: Record<string, string>;
  mode: string;
  audio_voices: string;
  status: string;
  published_at: string | null;
  content_version: number;
  sort_order: number;
  category: string;
  text_color: string;
  background_color: string;
  badge_background: string;
  requires_subscription: boolean;
  flashcard_enabled: boolean;
  flashcard_card_color: string;
  blocks: AdminTermSheetBlock[];
  validation_errors: string[];
};

export type TermSheetMediaUpload = {
  original_filename: string;
  cdn_key: string;
  url: string;
  content_type?: string | null;
  voice?: string | null;
};

export type TermSheetMatchResponse = {
  assignments: Array<{
    term_index: number;
    sound_adult_filename: string | null;
    sound_children_filename: string | null;
    confidence: number;
    notes: string;
  }>;
  unmatched_audio: string[];
};

export type TermSheetIngestJob = {
  job_id: string;
  status: string;
  result: {
    converted?: Array<{
      original_filename: string;
      raw_cdn_key: string;
      cdn_key: string;
      url?: string;
      voice?: string | null;
    }>;
    failed?: Array<{
      original_filename?: string;
      cdn_key?: string;
      error: string;
    }>;
  } | null;
  error_message: string | null;
};

export const TERM_SHEET_BLOCK_TYPES = [
  'intro',
  'section_header',
  'prose',
  'term_list',
  'divider',
] as const;

export type StorybookListItem = {
  id: number;
  slug: string;
  name: string;
  mode: string;
  audio_voices: string;
  reader_type: string;
  status: string;
  published_at: string | null;
  sort_order: number;
  content_version: number;
  page_count: number;
  requires_subscription: boolean;
};

export type AdminStorybookPage = {
  id: number;
  sort_order: number;
  title: string;
  image_url: string | null;
  image_tablet_url: string | null;
  sound_url: string | null;
  sound_children_url: string | null;
  audio_optional: boolean;
  image_cdn_key: string | null;
  image_tablet_cdn_key: string | null;
  sound_adult_cdn_key: string | null;
  sound_children_cdn_key: string | null;
};

export type AdminStorybook = {
  id: number;
  slug: string;
  name: string;
  title_translations: Record<string, string>;
  mode: string;
  audio_voices: string;
  reader_type: string;
  status: string;
  published_at: string | null;
  content_version: number;
  sort_order: number;
  category: string;
  text_color: string;
  background_color: string;
  badge_background: string;
  cover_image_cdn_key: string | null;
  cover_image_url: string | null;
  requires_subscription: boolean;
  pages: AdminStorybookPage[];
  validation_errors: string[];
};

export type StorybookMediaUpload = {
  original_filename: string;
  cdn_key: string;
  url: string;
  content_type?: string | null;
  voice?: string | null;
};

export type StorybookMatchAudioResponse = {
  assignments: Array<{
    page_index: number;
    sound_adult_filename: string | null;
    sound_children_filename: string | null;
    confidence: number;
    notes: string;
  }>;
  unmatched_audio: string[];
};

export type StorybookMatchImagesResponse = {
  assignments: Array<{
    page_index: number;
    image_filename: string | null;
    confidence: number;
    notes: string;
  }>;
  unmatched_images: string[];
};

/** Village map designer (outdoor town / farm / mountain). */

export type VillageMapDir = 0 | 1 | 2 | 3; // 0=down 1=up 2=left 3=right

export type OutdoorAreaId =
  | 'town'
  | 'farm'
  | 'passage'
  | 'mountain'
  | 'largeTown'
  | 'greatCity';

export type VillageMapLegendCategory =
  | 'terrain'
  | 'water'
  | 'farm'
  | 'mountain'
  | 'prop'
  | 'forage'
  | 'building'
  | 'structure'
  | 'furniture'
  | 'props';

export type VillageMapLegendEntry = {
  char: string;
  label: string;
  category: VillageMapLegendCategory;
  walkable: boolean;
};

/** Enterable building rooms keyed on outdoor doorTarget / buildingId. */
export type BuildingInteriorId =
  | 'home-pog-yawg'
  | 'home-player'
  | 'home-niam-txiv'
  | 'shop-clothing'
  | 'school';

export type InteriorKit = 'gold' | 'moss' | 'slate';

export type InteriorAreaDoc = {
  id: BuildingInteriorId;
  kind: 'house' | 'shop' | 'school';
  /** Room art kit (independent of outdoor façade peach/school kits). */
  kit: InteriorKit;
  /** Floor boards (defaults to `kit`). Player home uses gold on moss walls. */
  floorKit?: InteriorKit;
  /** Rug tint (defaults to `kit`). */
  rugKit?: InteriorKit;
  width: number; // fixed 10
  height: number; // fixed 8
  rows: string[];
  spawn: { x: number; y: number };
  exitCell: { x: number; y: number };
  residents?: { npcId: string; x: number; y: number }[];
  /** "x,y" -> word id taught when facing that furniture. */
  furnitureWords?: Record<string, string>;
};

export type VillageMapEdgeWarp = {
  x: number;
  y: number;
  dir: VillageMapDir;
  toArea: OutdoorAreaId;
  toX: number;
  toY: number;
  toDir: VillageMapDir;
};

/**
 * Building placement (stamp provenance + agent context). Optional overlay.
 * Houses use homeId; shops/school use buildingId — doorTarget may be a
 * future interior until the mobile game wires walk-in rooms.
 */
export type VillageMapBuilding = {
  stampId: string;
  displayName: string;
  kind?: 'house' | 'shop' | 'school';
  /** Canonical id: home-* / shop-* / school. */
  buildingId?: string;
  /** Houses only (seed / export compat). */
  homeId?: 'home-pog-yawg' | 'home-player' | 'home-niam-txiv';
  owner: string;
  kit: string;
  doorTarget: string;
  doorChar: string;
  origin: { x: number; y: number };
  door: { x: number; y: number };
};

/** Custom readable map sign (`!` cell). Text lives in overlay, not ASCII. */
export type VillageMapSign = {
  id: string;
  x: number;
  y: number;
  text: string;
};

/**
 * Outdoor NPC pin (overlay only — never an ASCII stamp).
 * Dialogue/quests stay in mobile `VILLAGE_NPCS`; map docs own tile + area.
 */
export type VillageMapNpc = {
  /** Catalog id (`pog`, `yawg`, …) — unique across the whole document. */
  id: string;
  x: number;
  y: number;
  /** Display name (denormalized from catalog for export/hover). */
  name?: string;
  /** Atlas sprite key (e.g. `npcPog`). */
  sprite?: string;
  /** Designer note (not shown in-game). */
  note?: string;
};

/** Known outdoor NPC catalog entry (picker + validation). */
export type VillageMapNpcCatalogEntry = {
  id: string;
  name: string;
  sprite: string;
};

/**
 * Outdoor animal instance pin (overlay only — never an ASCII stamp).
 * Kind templates (wander/sprites/wordId/scale) stay in mobile; map docs own
 * instance id + kind + home tile + area. Many instances may share one kind.
 */
export type VillageMapAnimal = {
  /** Unique instance id (`qaib`, `qaib-2`, uuid, …) — unique document-wide. */
  id: string;
  /** Catalog kind (`qaib`, `npua`, …). Not unique — duplicates allowed. */
  kind: string;
  x: number;
  y: number;
  /** Display name (denormalized from catalog for export/hover). */
  name?: string;
  /** Atlas sprite key (e.g. `chicken0`). */
  sprite?: string;
  /** Designer note (not shown in-game). */
  note?: string;
};

/** Known outdoor animal kind (picker + validation). */
export type VillageMapAnimalCatalogEntry = {
  id: string;
  name: string;
  sprite: string;
};

export type OutdoorAreaDoc = {
  id: OutdoorAreaId;
  width: number;
  height: number;
  rows: string[];
  tips: Record<string, string>;
  /**
   * Padlocked door (`\``) toast text by `"x,y"` — same shape as tips.
   * Editable in the map editor; mobile bake reads lockMessageByCell.
   */
  lockMessages?: Record<string, string>;
  edgeWarps: VillageMapEdgeWarp[];
  /** Owned buildings placed via stamps (or seeded). ASCII still encodes kit. */
  buildings?: VillageMapBuilding[];
  /** Custom map signs (`!` tiles). Text is free-form; ASCII only places the sprite. */
  signs?: VillageMapSign[];
  /** Outdoor NPC pins (overlay). One spot per id across the whole document. */
  npcs?: VillageMapNpc[];
  /** Outdoor animal instance pins (overlay). Unique by instance id; kind may repeat. */
  animals?: VillageMapAnimal[];
};

export type VillageMapDocument = {
  version: 1;
  kind: 'hmong-village-maps';
  playerStart: { areaId: OutdoorAreaId; x: number; y: number };
  outdoor: Record<OutdoorAreaId, OutdoorAreaDoc>;
  /** Building interiors; missing keys are filled from seed on load/patch. */
  interiors: Record<BuildingInteriorId, InteriorAreaDoc>;
};

export type VillageMapListItem = {
  id: number;
  name: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type VillageMapRecord = VillageMapListItem & {
  document: VillageMapDocument;
  warnings: string[];
};

export type VillageMapLegend = {
  legend: VillageMapLegendEntry[];
  dimensions: Record<OutdoorAreaId, { width: number; height: number }>;
  dirs: Record<string, string>;
  /** Known outdoor NPCs for the designer picker (synced with mobile VILLAGE_NPCS). */
  outdoorNpcs?: VillageMapNpcCatalogEntry[];
  /** Known outdoor animal kinds for the designer picker (synced with mobile templates). */
  outdoorAnimals?: VillageMapAnimalCatalogEntry[];
  /** Interior palette (structure / furniture / props). */
  interiorLegend?: VillageMapLegendEntry[];
  interiorDimensions?: { width: number; height: number };
  interiorIds?: BuildingInteriorId[];
  interiorKits?: InteriorKit[];
};
